// Guides + role-specific complete walkthroughs for SupportPage.
// Edit this file to update help content — no code changes needed.

export const howToGuides = [
  {
    id: "creating-clips",
    title: "How to Create a Clip",
    summary: "Save the moments that matter — turn a 90-minute game into a 6-second highlight.",
    audience: "Coach, Player/Parent",
    steps: [
      { h: "Open the game", b: "Sign in → tap Games (or pick a recent game from your home page) → tap the game video you want to clip." },
      { h: "Find your moment", b: "Use the scrub bar (drag the dot) or arrow keys (← / →) to step frame-by-frame. Press space to pause/play." },
      { h: "Set the START", b: "When you're at the spot where the play begins, tap the SET START button (left side of the video controls). A flag appears on the timeline." },
      { h: "Set the END", b: "Scrub forward to where the play finishes, then tap SET END. The blue band between the flags is your clip." },
      { h: "Tag the clip", b: "Tap CREATE CLIP. A box pops up — give it a title (e.g. \"Touchdown - Q3\"), pick a tag (Touchdown / Tackle / Highlight / Penalty), then tap Save." },
      { h: "Share it", b: "Your clip appears in your Clips library. Tap any clip → Share → choose Instagram, Twitter, TikTok, or copy a public link to text it." },
    ],
    tips: [
      "Keep clips under 15 seconds for best engagement on social media.",
      "Tag the player you want to feature so they can find their clips on their own profile.",
      "Use the zoom button (top right of the player) to crop in on a specific player or part of the field before tapping CREATE CLIP — the zoom carries into the clip.",
    ],
  },
  {
    id: "creating-highlights",
    title: "How to Create a Highlight Tape",
    summary: "Stitch your best clips into a recruit-ready reel in under 5 minutes.",
    audience: "Coach, Player/Parent",
    steps: [
      { h: "Build your clip library first", b: "Highlight tapes are made FROM clips, so you'll want at least 3-5 clips already saved. (See \"How to Create a Clip\" above.)" },
      { h: "Open Highlights", b: "From the home page, tap Highlights (or in the menu: Highlights → New Highlight Tape)." },
      { h: "Pick clips to include", b: "You'll see all your saved clips. Tap each one you want in your reel — selected clips show a blue check. Tip: order matters, so tap them in the order you want them to play." },
      { h: "Reorder & trim", b: "Drag clips up/down to rearrange. Tap any clip in the timeline to trim its in/out point further if needed." },
      { h: "Title & save", b: "Give the highlight tape a name (e.g. \"Marcus Bond - 2025 Season Reel\") and tap CREATE HIGHLIGHT." },
      { h: "Share", b: "Once rendered, the tape is in your Highlights library. Tap → Share → social media, link, or download to your camera roll." },
    ],
    tips: [
      "Lead with your best clip — recruiters watch the first 10 seconds before deciding to keep watching.",
      "Mix clip types: don't put all touchdowns in a row. Alternate big plays / hustle / leadership moments.",
      "For college recruiting reels: keep it under 4 minutes total. Coaches stop watching after that.",
    ],
  },
  {
    id: "adding-approving-users",
    title: "How to Add & Approve Users",
    summary: "Three ways to onboard your team — invite link, direct add, or signup approval.",
    audience: "YSV Admin, League Admin, Org Admin, Coach",
    steps: [
      { h: "Path 1 — Invite Link (easiest, scales)", b: "Admin → Invite tab → Invite Link → pick the role you're inviting (Coach, Player/Parent, etc.) → assign Org/Team → tap GENERATE LINK. Copy the link and text/email it. Anyone who clicks creates their own account; you'll get a notification to approve them in the Approvals tab." },
      { h: "Path 2 — Direct Add (you create the account)", b: "Admin → Invite tab → Direct Add → fill in the user's name, email, temp password, role, AND assign their Org/Team. They're auto-approved (no pending step) and can log in immediately. Best for trusted users you've already talked to." },
      { h: "Path 3 — Signup Approval (they sign up themselves)", b: "If a user signs up directly via /get-started, they land in your Approvals tab. Open Admin → Approvals → review the row → tap APPROVE (green) or REJECT (red). Approved users instantly get access; rejected users get a notification email." },
      { h: "For multi-team coaches", b: "If a coach needs access to a team in a different org, they request it from their Profile → Other Teams → Request Access. The target org's admin sees the request in Approvals and approves it. The team is added to the coach's affiliations without changing their primary team." },
    ],
    tips: [
      "Invite Links are reusable by default — the same link can onboard your whole team. They expire 14 days after creation.",
      "Use Direct Add when speed matters: e.g., adding a coach 30 min before game time. They skip the approval queue entirely.",
      "Approval emails are sent automatically when you approve/reject. The user gets notified instantly.",
    ],
  },
];

// Role-specific complete walkthroughs — one per persona.
export const roleGuides = [
  {
    id: "league-admin",
    title: "Complete Guide for League Admins",
    persona: "League Admin",
    intro: "You oversee an entire league — multiple organizations, dozens of teams, hundreds of users. You're the quality control layer above org admins.",
    sections: [
      {
        h: "1. League setup (one-time, ~10 min)",
        b: [
          "Sign up via /league-signup or accept your YSV onboarding email.",
          "Once approved, log in → /admin → confirm your league name and details under the Leagues tab.",
          "Add organizations: /admin → Organizations → + Add Org. Each org represents a club/program in your league.",
          "Once an org admin is invited, they can add their own teams.",
        ],
      },
      {
        h: "2. Approving Org Admins",
        b: [
          "When someone signs up to admin an organization in your league, they land in your Approvals tab.",
          "/admin → Approvals → review name, email, organization assignment.",
          "Approve only if you recognize the person and the org is legit. Reject if it looks like a duplicate or unauthorized signup.",
          "Approved org admins can then add their own coaches, players, and parents — you don't have to manage every user yourself.",
        ],
      },
      {
        h: "3. Game day visibility",
        b: [
          "/games shows every game from every org/team in your league. Use the search filter to narrow by team or date.",
          "You can watch any game uploaded by any GDO in your league.",
          "If a game was uploaded to the wrong team, you can flag it — the YSV admin will reassign it.",
        ],
      },
      {
        h: "4. Resolving cross-org disputes",
        b: [
          "If a coach from Org A wants viewing rights to a game vs Org B (e.g., scouting the next opponent), they request it from Profile → Other Teams.",
          "If both orgs are in your league, you'll see the request in your Approvals tab. Approve to grant access.",
          "For scouting requests outside your league, the YSV admin handles it.",
        ],
      },
      {
        h: "5. League analytics",
        b: [
          "/admin → Overview shows total games uploaded, active subscribers, team participation rates.",
          "Export reports as PDF (top right of the Overview tab) for your league board meetings.",
        ],
      },
    ],
  },
  {
    id: "org-admin",
    title: "Complete Guide for Org Admins",
    persona: "Org Admin",
    intro: "You run a single organization (e.g., a club program with multiple age groups). You manage your teams, coaches, players, and the season's logistics.",
    sections: [
      {
        h: "1. Setup (first 30 min)",
        b: [
          "Get invited by your league admin (or sign up directly and wait for approval).",
          "/admin → Teams → + Add Team for each age group / squad in your org (e.g., \"Hellcats 11U\", \"Hellcats 13U\").",
          "/admin → Invite → for each team, add the head coach via Direct Add (fastest) or send them an Invite Link.",
          "Coaches then handle the player/parent onboarding for their own teams.",
        ],
      },
      {
        h: "2. Approving coaches & players",
        b: [
          "When a coach or player signs up, they land in your Approvals tab.",
          "/admin → Approvals → review → Approve (green) or Reject (red).",
          "Approved users get a welcome email automatically; they can log in immediately.",
        ],
      },
      {
        h: "3. Schedule & game management",
        b: [
          "/admin → Schedules → + New Schedule → enter date, time, both teams (home + away), location.",
          "Assign a GDO (Game Day Operator) to record. The GDO gets a notification with a unique upload link.",
          "After the game, the GDO uploads the video. It auto-attaches to both teams' game pages.",
          "Update the score from /games → tap the game → Post-game scoring (your authority lets you set both teams' scores even if they're in another org).",
        ],
      },
      {
        h: "4. AV (age verification) & cross-org matchups",
        b: [
          "Before playing another org, both sides exchange AV docs to confirm player ages.",
          "/admin → Matchup Manager → New Matchup → pick the opposing team → Request AV.",
          "The opposing org admin uploads their roster + AV docs. You review/approve. They do the same for yours.",
          "Once both sides are AV-verified, the game is greenlit.",
        ],
      },
      {
        h: "5. Multi-team coach approvals",
        b: [
          "If a coach in your org needs to also coach a team in another org, they request access from their Profile.",
          "If they're requesting a team in YOUR org, you'll see the request in Approvals → approve to add the affiliation.",
          "If they're requesting a team in another org, that org's admin handles it.",
        ],
      },
    ],
  },
  {
    id: "coach",
    title: "Complete Guide for Coaches",
    persona: "Coach",
    intro: "You run a team. YSV gives you game video, clips, highlights, scouting, and a way to engage your players' families — all in one app.",
    sections: [
      {
        h: "1. First-time setup (5 min)",
        b: [
          "Get invited by your org admin (Direct Add or Invite Link) → click the link → set your password → done.",
          "If you signed up yourself, you'll wait for your org admin to approve. Allow up to 24 hours.",
          "Once approved, /home shows your team's upcoming games and recent clips.",
        ],
      },
      {
        h: "2. Onboarding your players & parents",
        b: [
          "Easiest: /admin → Invite tab → Invite Link → role: Player/Parent → assign your team → GENERATE LINK.",
          "Text/email the link to all your team families. They sign up themselves.",
          "Each signup needs Parent Email — the parent gets a one-click claim link by email so they can also create a parent account linked to their child.",
          "You'll get notifications as families sign up. Approve them from /admin → Approvals.",
        ],
      },
      {
        h: "3. Watching games & creating clips",
        b: [
          "After a game, the GDO uploads the video. You'll see it on /games within an hour or two.",
          "Tap the game → use SET START / SET END → CREATE CLIP for any moment.",
          "Tag clips with the player number (jersey #) — players will find their own clips on their profile.",
          "See \"How to Create a Clip\" above for the full step-by-step.",
        ],
      },
      {
        h: "4. Sharing clips with players & parents",
        b: [
          "Open any clip → tap the share icon → SHARE TO PLAYER → pick the player from your team roster.",
          "They get a notification + the clip lands in their personal library.",
          "Or copy a public link and post in your team group chat.",
        ],
      },
      {
        h: "5. Building highlight tapes",
        b: [
          "End-of-season recruiting reels or just for-fun team highlights.",
          "/highlights → + New Highlight Tape → pick clips → reorder → CREATE.",
          "Share the final tape with the whole team or with a specific recruiter.",
          "See \"How to Create a Highlight Tape\" above for the full step-by-step.",
        ],
      },
      {
        h: "6. Scouting other teams (multi-team access)",
        b: [
          "Want to watch your next opponent's previous game? Profile → Other Teams → REQUEST ACCESS.",
          "Pick the target league → org → team → optional reason (\"scouting Saturday's game\").",
          "The target org's admin (or league admin) approves. Once approved, that team's games show up in your /games list.",
        ],
      },
    ],
  },
  {
    id: "player-parent",
    title: "Complete Guide for Players & Parents",
    persona: "Player / Parent",
    intro: "Watch every game. Save your best plays. Build your personal highlight reel. YSV is your season, captured.",
    sections: [
      {
        h: "1. Sign up & get approved",
        b: [
          "Got an invite link from your coach? Click it → fill in name, email, password, parent email → submit. Wait for your coach or org admin to approve (usually <24 hrs).",
          "If you signed up directly via /get-started, pick role \"Player/Parent\", enter your team, and provide a parent email. Same approval flow.",
          "Parents: you'll get a separate one-click email to claim your own free parent account linked to your child. Click it → set a password → you're in.",
        ],
      },
      {
        h: "2. Subscribing for full access",
        b: [
          "After approval, you'll see games but they're locked behind the YSV Member subscription.",
          // Deliberately price-free: this is a static copy module with no access
          // to live pricing, and prices now come from Stripe. The current rates
          // are shown on the Subscribe screen and the Pricing page.
          "Profile → Subscribe → pick Monthly or Annual (annual costs less than 12 months of monthly). Current rates are shown on the Pricing page.",
          "Have a promo code? Enter it at Stripe checkout (e.g., \"SpringBeta26\" gets you 2 months free).",
          "Card details are processed by Stripe, never stored on YSV.",
          "Cancel anytime: Profile → Manage Subscription.",
        ],
      },
      {
        h: "3. Watching games",
        b: [
          "/games shows every game your team played. Tap to watch.",
          "Use the scrub bar to jump around. Use the zoom button to crop in on the action.",
          "Watch on phone, tablet, or laptop — your subscription covers all devices.",
        ],
      },
      {
        h: "4. Creating clips",
        b: [
          "While watching: SET START → SET END → CREATE CLIP → name + tag → save.",
          "Clips land in your Clips library — only you and your coach can see them by default.",
          "Tap any clip → Share → Instagram, TikTok, or copy a public link.",
          "See \"How to Create a Clip\" above for full details.",
        ],
      },
      {
        h: "5. Building your highlight tape (recruiting!)",
        b: [
          "Once you have 5+ clips, build a recruiting reel.",
          "/highlights → + New Highlight Tape → select your best clips in the order you want.",
          "Open with your most explosive play. Keep the reel under 4 minutes.",
          "Download the final tape OR share a public link with college coaches.",
          "See \"How to Create a Highlight Tape\" for tips on what coaches actually want to see.",
        ],
      },
      {
        h: "6. Updating your profile",
        b: [
          "Profile → upload a profile photo, set your jersey number, and (players) your date of birth.",
          "Add your position so coaches can find you.",
          "If you switch teams mid-season, your new coach will re-affiliate you — your past clips and stats stay with you.",
        ],
      },
    ],
  },
];
